import { Routes, Route, Navigate } from 'react-router-dom';
import { Spinner } from '@daycal/ui';
import { useAuth } from './contexts/AuthContext.jsx';
import Layout from './components/Layout.jsx';
import Landing from './pages/Landing.jsx';
import Login from './pages/Login.jsx';
import Signup from './pages/Signup.jsx';
import Team from './pages/Team.jsx';
import Contact from './pages/Contact.jsx';
import Deal from './pages/Deal.jsx';
import Privacy from './pages/Privacy.jsx';
import Terms from './pages/Terms.jsx';
import Refund from './pages/Refund.jsx';
import { EMPLOYEE_HOME } from './lib/config.js';
import Dashboard from './pages/Dashboard.jsx';
import EventTypes from './pages/EventTypes.jsx';
import Availability from './pages/Availability.jsx';
import FormBuilder from './pages/FormBuilder.jsx';
import Bookings from './pages/Bookings.jsx';
import BookingCalendar from './pages/BookingCalendar.jsx';
import PromoCodes from './pages/PromoCodes.jsx';
import Settings from './pages/Settings.jsx';
import UpgradePlan from './pages/UpgradePlan.jsx';
import AdminUsers from './pages/admin/AdminUsers.jsx';
import AdminPlans from './pages/admin/AdminPlans.jsx';
import AdminPromos from './pages/admin/AdminPromos.jsx';
import AdminBookings from './pages/admin/AdminBookings.jsx';
import AdminPayments from './pages/admin/AdminPayments.jsx';
import AdminSettings from './pages/admin/AdminSettings.jsx';
import AdminLegalPages from './pages/admin/AdminLegalPages.jsx';
import AdminAudit from './pages/admin/AdminAudit.jsx';
import AdminApiDocs from './pages/admin/AdminApiDocs.jsx';

function Protected({ children, admin = false, ownerOnly = false }) {
  const { isAuthenticated, isSuperAdmin, isEmployee, loading } = useAuth();
  if (loading) return <Centered><Spinner /></Centered>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (admin && !isSuperAdmin) return <Navigate to="/dashboard" replace />;
  // Agency employees are scoped to their own data — keep them off owner-only surfaces.
  if (ownerOnly && isEmployee) return <Navigate to={EMPLOYEE_HOME} replace />;
  return children;
}

// Routing + guards (architecture.md §19). "/" is the public marketing Landing
// for everyone; the authenticated app lives under /dashboard/*. /admin/* requires
// SUPER_ADMIN.
export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/deal" element={<Deal />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/refund" element={<Refund />} />
      <Route path="/refund-policy" element={<Refund />} />
      <Route path="/cancellation-refund" element={<Refund />} />

      <Route
        element={
          <Protected>
            <Layout />
          </Protected>
        }
      >
        {/* Owner-only surfaces — employees are redirected to their scoped home. */}
        <Route path="/dashboard" element={<Protected ownerOnly><Dashboard /></Protected>} />
        <Route path="/event-types" element={<Protected ownerOnly><EventTypes /></Protected>} />
        <Route path="/form" element={<Protected ownerOnly><FormBuilder /></Protected>} />
        <Route path="/team" element={<Protected ownerOnly><Team /></Protected>} />
        <Route path="/promo-codes" element={<Protected ownerOnly><PromoCodes /></Protected>} />
        <Route path="/settings" element={<Protected ownerOnly><Settings /></Protected>} />
        <Route path="/upgrade" element={<Protected ownerOnly><UpgradePlan /></Protected>} />
        {/* Shared surfaces available to employees too. */}
        <Route path="/availability" element={<Availability />} />
        <Route path="/bookings" element={<Bookings />} />
        <Route path="/booking-calendar" element={<BookingCalendar />} />
        <Route path="/admin/users" element={<Protected admin><AdminUsers /></Protected>} />
        <Route path="/admin/plans" element={<Protected admin><AdminPlans /></Protected>} />
        <Route path="/admin/promo-codes" element={<Protected admin><AdminPromos /></Protected>} />
        <Route path="/admin/bookings" element={<Protected admin><AdminBookings /></Protected>} />
        <Route path="/admin/payments" element={<Protected admin><AdminPayments /></Protected>} />
        <Route path="/admin/settings" element={<Protected admin><AdminSettings /></Protected>} />
        <Route path="/admin/legal-pages" element={<Protected admin><AdminLegalPages /></Protected>} />
        <Route path="/admin/audit" element={<Protected admin><AdminAudit /></Protected>} />
        <Route path="/admin/api-docs" element={<Protected admin><AdminApiDocs /></Protected>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function Centered({ children }) {
  return <div className="flex min-h-screen items-center justify-center">{children}</div>;
}
